import { AxiosError } from 'axios';
import pMap from 'p-map';
import type { DocumentExtractionV2Invocation } from '@lifeomic/react-client';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import {
  buildSummaryArtifactResourceIds,
  getSourceReference,
  isTaskForSource,
  selectSourceTask,
} from './documentExtractionV2Source';
import { isDocumentExtractionV2InFlight } from './DocumentExtractionV2Tasks';

const documentReferenceFhirService = new FhirService('DocumentReference');
const provenanceFhirService = new FhirService('Provenance');

export const DOCUMENT_EXTRACTION_STATUS_REQUEST_CONCURRENCY = 3;

export const getDocumentExtractionTask = (
  tasks: DocumentExtractionV2Invocation[],
  documentReferenceId: string,
) => {
  const sourceTasks = tasks.filter(task =>
    isTaskForSource(task, 'DocumentReference', documentReferenceId),
  );

  return (
    sourceTasks.find(
      task =>
        isDocumentExtractionV2InFlight(task) && task.status === 'processing',
    ) ??
    sourceTasks.find(isDocumentExtractionV2InFlight) ??
    selectSourceTask(tasks, 'DocumentReference', documentReferenceId)
  );
};

export const needsDurableExtractionStatusCheck = (
  documentReferenceId: string,
  tasks: DocumentExtractionV2Invocation[],
) => getDocumentExtractionTask(tasks, documentReferenceId) == null;

const hasSummaryArtifact = async ({
  accountId,
  documentReferenceId,
  memberId,
  projectId,
  serviceAuth,
}: {
  accountId: string;
  documentReferenceId: string;
  memberId: string;
  projectId: string;
  serviceAuth: {
    account: string;
    authToken: string;
  };
}) => {
  const summaryDocumentReferenceIds = await buildSummaryArtifactResourceIds({
    accountId,
    projectId,
    subjectId: memberId,
    sourceResourceType: 'DocumentReference',
    sourceResourceId: documentReferenceId,
  });

  for (const summaryDocumentReferenceId of summaryDocumentReferenceIds) {
    try {
      await documentReferenceFhirService.getById({
        account: serviceAuth.account,
        authToken: serviceAuth.authToken,
        config: {},
      })(accountId, summaryDocumentReferenceId);
      return true;
    } catch (error) {
      if ((error as AxiosError).response?.status !== 404) {
        throw error;
      }
    }
  }

  return false;
};

const hasExtractionProvenance = async ({
  accountId,
  documentReferenceId,
  projectId,
  serviceAuth,
}: {
  accountId: string;
  documentReferenceId: string;
  projectId: string;
  serviceAuth: {
    account: string;
    authToken: string;
  };
}) => {
  const response = await provenanceFhirService.search({
    account: serviceAuth.account,
    authToken: serviceAuth.authToken,
    config: {},
  })(accountId, projectId, {
    entity: getSourceReference('DocumentReference', documentReferenceId),
    _count: 1,
  });

  return !!response.data.entry?.length;
};

const hasDurableDocumentExtraction = async ({
  accountId,
  documentReferenceId,
  memberId,
  projectId,
  serviceAuth,
}: {
  accountId: string;
  documentReferenceId: string;
  memberId: string;
  projectId: string;
  serviceAuth: {
    account: string;
    authToken: string;
  };
}) => {
  try {
    return (
      (await hasSummaryArtifact({
        accountId,
        documentReferenceId,
        memberId,
        projectId,
        serviceAuth,
      })) ||
      (await hasExtractionProvenance({
        accountId,
        documentReferenceId,
        projectId,
        serviceAuth,
      }))
    );
  } catch {
    return false;
  }
};

export const loadDurableExtractedDocumentIds = async ({
  accountId,
  documentReferenceIds,
  memberId,
  projectId,
  serviceAuth,
  concurrency = DOCUMENT_EXTRACTION_STATUS_REQUEST_CONCURRENCY,
}: {
  accountId: string;
  documentReferenceIds: string[];
  memberId: string;
  projectId: string;
  serviceAuth: {
    account: string;
    authToken: string;
  };
  concurrency?: number;
}) => {
  if (!documentReferenceIds.length) {
    return [];
  }

  const extractedDocumentIds = await pMap(
    documentReferenceIds,
    async documentReferenceId => {
      const hasDurableExtraction = await hasDurableDocumentExtraction({
        accountId,
        documentReferenceId,
        memberId,
        projectId,
        serviceAuth,
      });

      return hasDurableExtraction ? documentReferenceId : null;
    },
    { concurrency },
  );

  return Array.from(
    new Set(
      extractedDocumentIds.filter(
        (documentReferenceId): documentReferenceId is string =>
          !!documentReferenceId,
      ),
    ),
  );
};
